import React, { useState, useEffect, useCallback } from 'react';
import { remindersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  BellRing, Loader2, Sparkles, Send, Building2, Hash, MapPin, CalendarClock,
  CheckCircle2, Mail, AlertTriangle, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export const RemindersPage = () => {
  const [settings, setSettings] = useState({ auto_enabled: true, days_before: 2 });
  const [due, setDue] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // send dialog
  const [dialogJob, setDialogJob] = useState(null);
  const [draft, setDraft] = useState({ subject: '', body: '' });
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [dueRes, sentRes] = await Promise.all([remindersAPI.due(), remindersAPI.sent()]);
      setDue(dueRes.data.reminders || []);
      setSettings(dueRes.data.settings || { auto_enabled: true, days_before: 2 });
      setSent(sentRes.data || []);
    } catch (e) {
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    setSavingSettings(true);
    try {
      const res = await remindersAPI.updateSettings(patch);
      setSettings(res.data);
      toast.success('Reminder settings updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const openSend = async (r) => {
    setDialogJob(r);
    setDraft({ subject: '', body: '' });
    setDrafting(true);
    try {
      const res = await remindersAPI.draft(r.job_id);
      setDraft({ subject: res.data.subject || '', body: res.data.body || '' });
    } catch (e) {
      toast.error('Failed to draft email');
    } finally {
      setDrafting(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await remindersAPI.send({ job_id: dialogJob.job_id, subject: draft.subject, body: draft.body });
      if (res.data?.email_delivered === false) {
        toast.warning(res.data.warning || `Couldn't send to ${dialogJob.supplier_email}. Please check the address.`);
      } else {
        toast.success(`Reminder sent to ${dialogJob.supplier_email}`);
      }
      setDialogJob(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="reminders-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          Supplier Reminders
          <span className="text-xs font-semibold px-2 py-1 rounded-full brand-gradient text-white flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> AI
          </span>
        </h1>
        <p className="text-muted-foreground mt-1">Automatically ask suppliers for updates before you're due on site</p>
      </div>

      {/* Settings */}
      <Card className="surface">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center"><BellRing className="h-5 w-5 text-primary" /></div>
            <div>
              <p className="font-semibold">Automatic reminders</p>
              <p className="text-sm text-muted-foreground">Email suppliers automatically before each booking</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Label htmlFor="days" className="text-sm text-muted-foreground">Days before</Label>
              <Input
                id="days" type="number" min={0} max={14} value={settings.days_before}
                onChange={(e) => setSettings({ ...settings, days_before: parseInt(e.target.value, 10) || 0 })}
                onBlur={(e) => saveSettings({ days_before: parseInt(e.target.value, 10) || 0 })}
                className="w-20 bg-input border-border" data-testid="days-before"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{settings.auto_enabled ? 'On' : 'Off'}</span>
              <Switch checked={!!settings.auto_enabled} disabled={savingSettings} onCheckedChange={(v) => saveSettings({ auto_enabled: v })} data-testid="auto-toggle" />
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : (
        <>
          {/* Due now */}
          <div>
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Due in {settings.days_before} day{settings.days_before === 1 ? '' : 's'} ({due.length})
            </h2>
            {due.length === 0 ? (
              <Card className="surface"><CardContent className="py-12 text-center text-muted-foreground">
                <BellRing className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>No suppliers to remind right now.</p>
                <p className="text-sm">Reminders appear here {settings.days_before} days before an on-site date.</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {due.map((r) => (
                  <Card key={r.job_id} className="surface card-hover" data-testid={`due-${r.job_id}`}>
                    <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">{r.title}</h3>
                          {r.already_sent ? (
                            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Sent</Badge>
                          ) : (
                            <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">Pending</Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm text-muted-foreground">
                          {r.supplier_name && <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{r.supplier_name}</span>}
                          {r.po_reference && <span className="flex items-center gap-1 font-mono"><Hash className="h-3.5 w-3.5" />{r.po_reference}</span>}
                          {r.on_site_date && <span className="flex items-center gap-1 text-amber-400"><CalendarClock className="h-3.5 w-3.5" />{format(parseISO(r.on_site_date), 'd MMM yyyy')}</span>}
                          {r.address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{r.address}</span>}
                        </div>
                        <div className="mt-1.5 text-sm flex items-center gap-1">
                          {r.can_send ? (
                            <span className="flex items-center gap-1 text-muted-foreground"><Mail className="h-3.5 w-3.5" />{r.supplier_email}</span>
                          ) : (
                            <span className="flex items-center gap-1 text-rose-400"><AlertTriangle className="h-3.5 w-3.5" />No supplier email — add one on the supplier</span>
                          )}
                        </div>
                      </div>
                      <Button onClick={() => openSend(r)} disabled={!r.can_send} data-testid={`send-btn-${r.job_id}`}>
                        <Sparkles className="h-4 w-4 mr-1" /> {r.already_sent ? 'Send again' : 'Preview & send'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sent history */}
          {sent.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2"><History className="h-4 w-4" /> Sent ({sent.length})</h2>
              <Card className="surface"><CardContent className="p-0">
                {sent.map((s) => (
                  <div key={s.id} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                      <span className="truncate">{s.subject}</span>
                      {s.auto && <Badge variant="outline" className="text-[10px]">Auto</Badge>}
                    </div>
                    <span className="text-muted-foreground flex-shrink-0 ml-3">{s.supplier_email} · {s.sent_at ? format(parseISO(s.sent_at), 'd MMM, h:mma') : ''}</span>
                  </div>
                ))}
              </CardContent></Card>
            </div>
          )}
        </>
      )}

      {/* Send dialog */}
      <Dialog open={!!dialogJob} onOpenChange={(o) => !o && setDialogJob(null)}>
        <DialogContent className="bg-card border-border max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Reminder to {dialogJob?.supplier_name}</DialogTitle>
          </DialogHeader>
          {drafting ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
              <Loader2 className="h-7 w-7 animate-spin text-primary" /><p className="text-sm">AI is drafting the email…</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="h-4 w-4" /> To: {dialogJob?.supplier_email}</div>
              <div className="space-y-1.5">
                <Label>Subject</Label>
                <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="bg-input border-border" data-testid="draft-subject" />
              </div>
              <div className="space-y-1.5">
                <Label>Message</Label>
                <Textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={10} className="bg-input border-border text-sm" data-testid="draft-body" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogJob(null)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending || drafting || !draft.body} data-testid="confirm-send">
              {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-2" /> Send email</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemindersPage;
